import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ConvertNumber, ConvertToEnglishNumber, ConvertToPersianNumber, DeConvertNumber } from "@/utils";
import {
  formatCart,
  formatSheba,
} from "@/components";
import { Eye, EyeOff } from "lucide-react";

export const CustomInput: React.FC<{
  type:
    | "password"
    | "number"
    | "text_number"
    | "price"
    | "cart-number"
    | "sheba-number";
  name: string;
  value?: string;
  className: string;
  onChange: Function;
  placeholder?: string;
  dir?: "rtl" | "ltr";
  disabled?: boolean;
  lng?: string;
  onClick?: Function;
}> = ({
  type,
  name,
  value,
  onChange,
  className,
  dir,
  placeholder,
  disabled,
  lng,
  onClick = () => {},
}) => {
  if (type == "password") {
    return (
      <PasswordInput
        className={className}
        placeholder={placeholder ? placeholder : ""}
        name={name}
        value={value + ""}
        onChange={(data: string) => {
          onChange(data);
        }}
        onClick={onClick}
      />
    );
  }

  if (type == "price" || type == "number") {
    return (
      <NumberInput
        className={className}
        placeholder={placeholder ? placeholder : ""}
        name={name}
        value={value + ""}
        onChange={(data: string) => {
          onChange(data);
        }}
        disabled={disabled}
        isPrice={type == "price"}
        lng={lng == "fa" ? "fa" : "en"}
        onClick={onClick}
      />
    );
  }

  if (type == "text_number") {
    return (
      <NumberTextInput
        className={className}
        placeholder={placeholder ? placeholder : ""}
        name={name}
        value={value}
        onChange={(data: string) => {
          onChange(data);
        }}
        disabled={disabled}
        lng={lng == "fa" ? "fa" : "en"}
        dir={dir}
        onClick={onClick}
      />
    );
  }

  if (type == "cart-number" || type == "sheba-number") {
    return (
      <CartShebaNumberInput
        className={className}
        placeholder={placeholder ? placeholder : ""}
        name={name}
        value={value + ""}
        onChange={(data: string) => {
          onChange(data);
        }}
        disabled={disabled}
        isSheba={type == "sheba-number"}
        onClick={onClick}
      />
    );
  }
  return <></>;
};

const PasswordInput: React.FC<{
  placeholder?: string;
  name: string;
  value: string;
  className: string;
  onChange: Function;
  onClick: Function;
}> = ({ placeholder, name, value, onChange, className, onClick }) => {
  const persianContentRegex = /[۰-۹\u0600-\u06FF]/;
  const [errorText, setErrorText] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    if (errorText.length > 0) {
      toast.error(errorText, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  }, [errorText]);

  return (
    <div className={`custom-input d-flex flex-row ${className}`}>
      <span
        className={"g-cursor-pointer"}
        onClick={() => {
          setShowPassword(!showPassword);
        }}
      >
        {showPassword ? (
          <Eye size={"25"} className={"text4"} />
        ) : (
          <EyeOff size={"25"} className={"text4"} />
        )}
      </span>
      <input
        dir={"ltr"}
        className={"custom-input-none w-100"}
        placeholder={placeholder ? placeholder : ""}
        name={name}
        value={value}
        type={showPassword ? "text" : "password"}
        onChange={(e) => {
          if (!persianContentRegex.test(e.target.value)) {
            onChange(e.target.value);
            setErrorText("");
          } else {
            setErrorText("فقط حروف انگلیسی مجاز است");
          }
        }}
        autoComplete={"off"}
        onClick={() => {
          onClick();
        }}
      />
    </div>
  );
};

const NumberInput: React.FC<{
  placeholder?: string;
  name: string;
  value: string;
  className: string;
  onChange: Function;
  isPrice: boolean;
  disabled?: boolean;
  lng?: "fa" | "en";
  onClick: Function;
}> = ({
  placeholder,
  name,
  value,
  onChange,
  className,
  disabled,
  isPrice,
  lng = "fa",
  onClick,
}) => {
  const [changeValue, setChangeValue] = useState<string>("");

  useEffect(() => {
    if (lng == "fa") {
      setChangeValue(ConvertNumber(value, "fa", isPrice));
    } else {
      setChangeValue(ConvertNumber(value, "en", isPrice));
    }
  }, [value]);

  return (
    <input
      dir={"ltr"}
      className={className}
      placeholder={placeholder ? placeholder : ""}
      name={name}
      value={changeValue}
      type={"text"}
      onChange={(e) => {
        onChange(DeConvertNumber(e.target.value));
      }}
      disabled={disabled}
      autoComplete={"off"}
      onClick={() => {
        onClick();
      }}
    />
  );
};

const NumberTextInput: React.FC<{
  placeholder?: string;
  name: string;
  value?: string;
  className: string;
  onChange: Function;
  disabled?: boolean;
  lng?: "fa" | "en";
  dir?: "rtl" | "ltr";
  onClick: Function;
}> = ({
  placeholder,
  name,
  value,
  onChange,
  className,
  disabled,
  lng = "fa",
  dir,
  onClick,
}) => {
  const [changeValue, setChangeValue] = useState<string>("");

  useEffect(() => {
    if (value != undefined) {
      if (lng == "fa") {
        setChangeValue(ConvertToPersianNumber(value));
      } else {
        setChangeValue(ConvertToEnglishNumber(value));
      }
    }
  }, [value]);

  return (
    <input
      dir={dir}
      className={className}
      placeholder={placeholder ? placeholder : ""}
      name={name}
      value={changeValue}
      type={"text"}
      onChange={(e) => {
        onChange(ConvertToEnglishNumber(e.target.value));
      }}
      disabled={disabled}
      autoComplete={"off"}
      onClick={() => {
        onClick();
      }}
    />
  );
};

const CartShebaNumberInput: React.FC<{
  placeholder?: string;
  name: string;
  value: string;
  className: string;
  onChange: Function;
  isSheba: boolean;
  disabled?: boolean;
  onClick: Function;
}> = ({
  placeholder,
  name,
  value,
  onChange,
  className,
  disabled,
  isSheba,
  onClick,
}) => {
  const [changeValue, setChangeValue] = useState<string>("");
  useEffect(() => {
    if (isSheba) {
      setChangeValue(formatSheba(ConvertNumber(value, "en", false)));
    } else {
      setChangeValue(formatCart(ConvertNumber(value, "en", false)));
    }
  }, [value]);

  return (
    <input
      dir={"ltr"}
      className={className}
      placeholder={placeholder ? placeholder : ""}
      name={name}
      value={changeValue}
      type={"text"}
      onChange={(e) => {
        onChange(ConvertToEnglishNumber(e.target.value.replaceAll("-", "")));
      }}
      disabled={disabled}
      autoComplete={"off"}
      onClick={() => {
        onClick();
      }}
    />
  );
};
